import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import * as T from '../../components/Typography';
import colors from '../../theme/colors';

import {
  useGetSurveyPaymentDetails,
  useUpdateSurveyPaymentMethod
} from '../../api-calls/survey.queries';
import { useAuth } from '../../context/auth';
import Button from '../../components/Button';

import * as S from './style';
import { ControllerAutoComplete } from '../../components/Inputs/AutoComplete';
import { updatePaymentMethod as updatePaymentMethodValidation } from '../../validation/schemas';
import { routes } from '../../constants';
import { useEffect, useCallback, useState } from 'react';
import MultiSelectDropdown from '../../components/Inputs/MultiSelect';

const paymentOptions = [
  { value: 'card', label: 'Credit Card' },
  { value: 'invoice', label: 'Invoice' },
  {
    value: 'exempt',
    label: 'Exempt from payment (charity or fewer than 50 employees)'
  }
];

const sectoralReportsList = [{
  value: '600',
  label: 'Legal sector report'
}, {
  value: '600',
  label: 'Professional services sector report'
}, {
  value: '600',
  label: 'Financial services, banking and insurance sector report'
}];

const CompletePayment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedSectoralReports, setSelectedSectoralReports] = useState([]);
  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(updatePaymentMethodValidation)
  });

  const {
    data: surveyPaymentData = {},
    error: getSurveyPaymentDetailsError,
    isLoading,
  } = useGetSurveyPaymentDetails({
    salesforceContactId: user?.salesforceContactId
  });

  const {
    mutateAsync: updateFn,
    isPending,
    error: updateError
  } = useUpdateSurveyPaymentMethod(user?.salesforceContactId);

  useEffect(() => {
    if (surveyPaymentData?.paymentMethod) {
      setValue('paymentMethod', surveyPaymentData.paymentMethod);
      setSelectedSectoralReports(surveyPaymentData.sectoralReports?.split(';').map((report) => ({ value: '600', label: report })) || []);
    }
  }, [surveyPaymentData, setValue]);

  const updatePaymentMethod = async ({
    paymentMethod
  }: {
    paymentMethod: string;
  }) => {
    try {
      await updateFn({
        salesforceContactId: user?.salesforceContactId,
        sectoralReports: selectedSectoralReports,
        paymentMethod
      });
      navigate(routes.SUBMIT_SURVEY_SUCCESS);
    } catch (error) {
      console.error('Error updating payment method:', error);
    }
  };

  const createCardElement = useCallback(
    ({ title, value }: { title: string; value: string }) => (
      <S.CardElement>
        <T.TextSmallRegular color={colors.neutral.shades.shade600}>
          {title}
        </T.TextSmallRegular>
        <T.TextSmallBold color={colors.neutral.shades.shade600}>
          {value}
        </T.TextSmallBold>
      </S.CardElement>
    ),
    []
  );

  if (isLoading || isPending) {
    return (
      <Box
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="loading"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (getSurveyPaymentDetailsError) {
    return (
      <T.H4 color="red">Error: {getSurveyPaymentDetailsError.message}</T.H4>
    );
  }

  const { fee = 0, tax = 0, orgName = '' } = surveyPaymentData;
  const sectoralReportsCost = selectedSectoralReports?.reduce((acc, report) => acc + parseInt(report.value), 0) || 0;
  const totalAmount = `£${(fee + sectoralReportsCost + ((fee + sectoralReportsCost) * tax) / 100).toFixed(2)}`;

  return (
    <form onSubmit={handleSubmit(updatePaymentMethod)}>
      <S.HomeWrapper>
        <S.InnerWrapper>
          <T.H4 color={colors.neutral.shades.shade700} mb={5}>
            ADDITIONAL SECTOR REPORTS
          </T.H4>
          <T.TextSmallRegular mt={2} color={colors.neutral.shades.shade600}>
            In addition to your individual feedback report, we also offer sector reports for the legal, professional services, and financial, banking and insurance sectors. These contain industry trends and insights to help your organisation attract, recruit and progress the best talent from all backgrounds.
          </T.TextSmallRegular>
          <T.TextSmallRegular mt={4} mb={2} color={colors.neutral.shades.shade600}>
            If you would like to purchase one or more sectoral report(s) then please select from the list below to view the price. Submit to checkout.
          </T.TextSmallRegular>
          <T.TextSmallRegular mt={2} mb={2} color={colors.neutral.shades.shade600}>
            Please note: You are exempt from paying the Index Survey Fee if you are a charity or have fewer than 50 employees. However, sectoral report(s) are an additional product and so charities and organisations with fewer than 50 employees will be charged if selected for purchase.
          </T.TextSmallRegular>
          <Box sx={{ width: { xs: '100%', sm: '100%' } }} mb={5}>
            <MultiSelectDropdown
              items={sectoralReportsList}
              setSelectedItems={setSelectedSectoralReports}
              selectedItems={selectedSectoralReports}
              placeholder={'Select one or more sectoral report...'}
              label={''}
            />
          </Box>
          <T.H4 color={colors.neutral.shades.shade700}>
            SELECT YOUR PAYMENT METHOD
          </T.H4>
          <T.TextSmallRegular mt={2} color={colors.neutral.shades.shade600}>
            Almost there! Please review the totals below and select your preferred payment method to submit your survey.
          </T.TextSmallRegular>
          {/* <S.Card>
            {createCardElement({
              title: 'Payment for',
              value: orgName?.replace(/\b\w/g, (char) => char.toUpperCase())
            })}
          </S.Card> */}

          <S.Card>
            {createCardElement({
              title: 'Survey fee',
              value: `£${fee.toFixed(2)}`
            })}
            {createCardElement({
              title: 'Sectoral reports',
              value: `£${sectoralReportsCost.toFixed(2)}`
            })}
            {createCardElement({ title: 'VAT', value: `${tax}%` })}
            {createCardElement({ title: 'Total', value: totalAmount })}
          </S.Card>

          <S.Card>
            <T.TextSmallBold color={colors.neutral.shades.shade600}>
              Payment method
            </T.TextSmallBold>
            <Box sx={{ width: { xs: '100%', sm: '384px' } }}>
              <ControllerAutoComplete
                control={control}
                name="paymentMethod"
                placeholder="Select payment method"
                options={paymentOptions}
                listItemProps={(option) => ({
                  key: `option-${option.value}`,
                  label: option.label
                })}
              />
            </Box>
            {updateError && (
              <T.TextSmallRegular color={colors.red.main}>
                {updateError.message || 'An error occurred'}
              </T.TextSmallRegular>
            )}
          </S.Card>
        </S.InnerWrapper>

        <S.ButtonWrapper>
          <Button
            width="fit-content"
            variant="blue"
            onClick={() => navigate(-1)}
          >
            Previous Page
          </Button>
          <Button
            type="submit"
            variant="primary"
            width="fit-content"
            loading={isPending}
            disabled={isPending}
          >
            Submit
          </Button>
        </S.ButtonWrapper>
      </S.HomeWrapper>
    </form>
  );
};

export default CompletePayment;
